import { SxProps } from '@mui/material'

export interface SlidePanelStyles {
  scrim: SxProps
  wrapper: SxProps
  handle: SxProps
  handleBar: SxProps
  contentWrapper: SxProps
  heading: SxProps
  headingBlock: SxProps
  headingText: SxProps
  subheadingText: SxProps
  closeIcon: SxProps
  closeGlyph: SxProps
  mainContent: SxProps
}

/** Gutter the desktop card leaves around itself, matching the canvas pane's padding. */
const desktopInset = 16

export const slidePanelStyles: SlidePanelStyles = {
  // Mobile only: the desktop card covers the canvas and leaves the conversation
  // beside it live, so dimming the page there would be wrong.
  scrim: {
    position: 'absolute',
    inset: 0,
    zIndex: 9,
    display: { xs: 'block', lg: 'none' },
    backgroundColor: 'rgba(10, 13, 18, 0.45)',
  },
  // A bottom sheet on mobile; on desktop a floating card sitting exactly over
  // the canvas pane, aligned to the same 16px gutter.
  wrapper: {
    position: 'absolute',
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: 'background.default',
    left: { xs: 0, lg: 'auto' },
    right: { xs: 0, lg: desktopInset },
    top: { xs: 'auto', lg: desktopInset },
    bottom: { xs: 0, lg: desktopInset },
    width: { xs: '100%', lg: `calc(45% - ${desktopInset}px)` },
    height: { xs: '92%', lg: 'auto' },
    borderRadius: { xs: '20px 20px 0 0', lg: 4 },
    boxShadow: {
      xs: '0 -12px 32px rgba(10, 13, 18, 0.22)',
      lg: '0 16px 48px rgba(10, 13, 18, 0.16)',
    },
  },
  // Grab bar, so the sheet reads as a surface pulled up over the conversation.
  handle: {
    flexShrink: 0,
    display: { xs: 'flex', lg: 'none' },
    justifyContent: 'center',
    pt: 1.5,
    pb: 0.5,
  },
  handleBar: {
    width: 40,
    height: 4,
    borderRadius: 999,
    backgroundColor: 'utils.gray300',
  },
  contentWrapper: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  // Stays put while the body scrolls, so the title and close are always to hand.
  heading: {
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 1.5,
    px: { xs: 2, lg: 3 },
    pt: { xs: 1, lg: 2.5 },
    pb: 1.5,
  },
  // Holds the title and its subheading in a column beside the close button.
  // `minWidth: 0` so the title still ellipsizes: it does that by shrinking, which
  // a wrapper with the default `min-width: auto` would prevent.
  headingBlock: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 0.25,
  },
  headingText: {
    typography: 'textLgSemibold',
    color: 'text.primary900',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Wraps rather than truncating, unlike the title above it: a subheading carries
  // detail, and an ellipsis through it would hide the part the reader came for.
  subheadingText: {
    typography: 'textSm',
    color: 'text.secondary700',
  },
  // Same close target as the modal's: a 44px hit area holding a 24px glyph,
  // pulled a step into the corner.
  closeIcon: {
    flexShrink: 0,
    ml: 'auto',
    width: 44,
    height: 44,
    mt: -1,
    mr: -1,
    p: 0,
    borderRadius: '50%',
    color: 'action.disabled',
    '&:hover': {
      backgroundColor: 'utils.gray50',
      color: 'text.secondary700',
    },
  },
  // The cross SVG carries no intrinsic size, so without this it stretches to
  // fill the whole button.
  closeGlyph: {
    width: 24,
    height: 'auto',
  },
  mainContent: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    px: { xs: 2, lg: 3 },
    pb: 3,
  },
}
